import React, { useEffect, useState } from 'react';
import { fmtDate, personColor } from './utils';

const PRIORITY_ORDER = ['Critical', 'Highest', 'High', 'Medium', 'Low', 'Lowest'];

// ─── IncidentMonthlyWidget ────────────────────────────────────────────────────
function IncidentMonthlyWidget({ api, uploadId, teamOnly }) {
  const [data,      setData]      = React.useState(null);
  const [loading,   setLoading]   = React.useState(true);
  const [error,     setError]     = React.useState(null);
  const [collapsed, setCollapsed] = React.useState(false);

  React.useEffect(() => {
    if (!uploadId) { setLoading(false); return; }
    setLoading(true); setError(null);
    fetch(`${api}/api/uploads/${uploadId}/incidents-monthly?team_only=${!!teamOnly}`)
      .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(d => setData(d))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [api, uploadId, teamOnly]);

  const curr = data?.current;
  const prev = data?.previous;

  const technicians = React.useMemo(() => {
    if (!curr && !prev) return [];
    const names = new Set([
      ...(curr?.by_assignee || []).map(a => a.name),
      ...(prev?.by_assignee || []).map(a => a.name),
    ]);
    const currMap = Object.fromEntries((curr?.by_assignee || []).map(a => [a.name, a.closed]));
    const prevMap = Object.fromEntries((prev?.by_assignee || []).map(a => [a.name, a.closed]));
    return [...names]
      .map(n => ({ name: n, curr: currMap[n] || 0, prev: prevMap[n] || 0 }))
      .sort((a, b) => b.curr - a.curr || b.prev - a.prev);
  }, [curr, prev]);

  const maxClosed = Math.max(1, ...technicians.map(t => Math.max(t.curr, t.prev)));
  const initials = name => name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  const StatBox = ({ label, value, color }) => (
    <div className="stat-box" style={{ background: color + '10', border: `1px solid ${color}30` }}>
      <div className="stat-box-value" style={{ color }}>{value}</div>
      <div className="stat-box-label">{label}</div>
    </div>
  );

  return (
    <div className="card card-spaced">
      <div className={collapsed ? 'widget-header widget-header--collapsed' : 'widget-header'}>
        <div className="widget-header-left">
          <div className="section-title section-title--inline">📋 Incidents — Monthly Overview</div>
          {!collapsed && curr && (
            <span className="muted-mono-sm">{curr.label} vs {prev?.label}</span>
          )}
        </div>
        <button onClick={() => setCollapsed(c => !c)} className="widget-toggle-btn">
          {collapsed ? 'Show' : 'Hide'}
        </button>
      </div>

      {!collapsed && loading && <div className="loading-text">Loading…</div>}
      {!collapsed && error   && <div className="error-text">Error: {error}</div>}

      {!collapsed && curr && prev && (
        <>
          <div className="monthly-grid">
            <div>
              <div className="monthly-label-curr">{curr.label}</div>
              <div className="monthly-stat-row">
                <StatBox label="Created" value={curr.created} color="#2563eb" />
                <StatBox label="Closed"  value={curr.closed}  color="#16a34a" />
              </div>
            </div>
            <div className="monthly-grid-divider" />
            <div>
              <div className="monthly-label-prev">{prev.label}</div>
              <div className="monthly-stat-row">
                <StatBox label="Created" value={prev.created} color="#94a3b8" />
                <StatBox label="Closed"  value={prev.closed}  color="#64748b" />
              </div>
            </div>
          </div>

          {technicians.length > 0 && (
            <>
              <div className="tech-breakdown-header">
                <span>Technician</span>
                <span className="tech-breakdown-months">
                  <span className="text-accent">{curr.label.split(' ')[0]}</span>
                  <span>{prev.label.split(' ')[0]}</span>
                </span>
              </div>
              <div className="tech-list">
                {technicians.map((t) => {
                  const color = personColor(t.name);
                  const currPct = (t.curr / maxClosed) * 100;
                  const prevPct = (t.prev / maxClosed) * 100;
                  return (
                    <div key={t.name}>
                      <div className="tech-row-header">
                        <div className="tech-avatar" style={{ background: color }}>{initials(t.name)}</div>
                        <span className="tech-name">{t.name}</span>
                        <div className="tech-counts">
                          <span className="tech-count-curr" style={{ color }}>{t.curr}</span>
                          <span className="tech-count-prev">{t.prev || '—'}</span>
                        </div>
                      </div>
                      <div className="dual-bar-wrap">
                        <div className="bar-thin bar-thin--curr">
                          <div className="bar-thin-fill" style={{ width: `${currPct}%`, background: color }} />
                        </div>
                        <div className="bar-thin">
                          <div className="bar-thin-fill" style={{ width: `${prevPct}%`, background: color + '55' }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {technicians.length === 0 && (
            <div className="italic-muted">No incidents closed in either month.</div>
          )}
        </>
      )}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

// ─── AgingIncidentsWidget ─────────────────────────────────────────────────────
function AgingIncidentsWidget({ api, uploadId, jiraUrl: widgetJiraUrl, teamOnly }) {
  const [tickets, setTickets]   = React.useState([]);
  const [loading, setLoading]   = React.useState(true);
  const [collapsed, setCollapsed] = React.useState(false);

  React.useEffect(() => {
    if (!uploadId) { setLoading(false); return; }
    setLoading(true);
    const jUrl = widgetJiraUrl || localStorage.getItem('jiraUrl') || '';
    const params = new URLSearchParams({ incident_filter: 'total', team_only: !!teamOnly });
    if (jUrl) params.set('jira_url', jUrl);
    fetch(`${api}/api/uploads/${uploadId}/tickets?${params}`)
      .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(d => {
        const filtered = (Array.isArray(d) ? d : [])
          .filter(t => t.age_days >= 1 && t.age_days <= 5)
          .sort((a, b) => b.age_days - a.age_days);
        setTickets(filtered);
      })
      .catch(() => setTickets([]))
      .finally(() => setLoading(false));
  }, [api, uploadId, teamOnly, widgetJiraUrl]);

  const dayColor = d => (['', '#16a34a', '#65a30d', '#d97706', '#ea580c', '#dc2626'])[Math.min(Math.floor(d), 5)] || '#94a3b8';

  const byDay = [1, 2, 3, 4, 5].map(day => ({
    day,
    count: tickets.filter(t => Math.floor(t.age_days) === day).length,
  }));

  return (
    <div className="card">
      <div className={collapsed ? 'widget-header widget-header--collapsed' : 'widget-header'}>
        <div className="widget-header-left">
          <div className="section-title section-title--inline">⏳ Open Incidents — Aging 1–5 Days</div>
          {!loading && <span className="badge badge-blue">{tickets.length}</span>}
        </div>
        <button onClick={() => setCollapsed(c => !c)} className="widget-toggle-btn">
          {collapsed ? 'Show' : 'Hide'}
        </button>
      </div>

      {!collapsed && (
        <>
          {!loading && tickets.length > 0 && (
            <div className="day-chips">
              {byDay.map(({ day, count }) => count > 0 && (
                <div key={day} className="day-chip"
                  style={{ background: dayColor(day) + '18', borderColor: dayColor(day) + '44', color: dayColor(day) }}>
                  <span className="mono">{day}d</span>
                  <span className="text-muted">·</span>
                  <span className="mono">{count}</span>
                </div>
              ))}
            </div>
          )}

          {loading && <div className="aging-loading">Loading…</div>}

          {!loading && tickets.length === 0 && (
            <div className="aging-empty">No open incidents aged 1–5 days. 🎉</div>
          )}

          {!loading && tickets.length > 0 && (
            <div className="incident-ticker">
              {tickets.map((t) => {
                const day   = Math.floor(t.age_days);
                const color = dayColor(day);
                const initials = (t.assignee || 'U').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
                return (
                  <div key={t.key} className="incident-row">
                    <div className="age-block" style={{ background: color + '18', borderColor: color + '44' }}>
                      <span style={{ color }}>{day}d</span>
                    </div>
                    {t.url
                      ? <a href={t.url} target="_blank" rel="noopener noreferrer" className="issue-key-link">{t.key} ↗</a>
                      : <span className="issue-key-link">{t.key}</span>
                    }
                    {priorityBadge(t.priority)}
                    <span className="aging-summary-text">{t.summary}</span>
                    {t.created && <span className="mono" style={{ fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{fmtDate(t.created)}</span>}
                    <div title={t.assignee || 'Unassigned'} className="aging-assignee-avatar">{initials}</div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

function priorityBadge(p) {
  const cls = {
    Critical: 'badge-critical', Highest: 'badge-critical',
    High: 'badge-high',
    Medium: 'badge-medium',
    Low: 'badge-low', Lowest: 'badge-low',
  }[p] || 'badge-default';
  return <span className={`badge ${cls}`}>{p}</span>;
}

function ageBar(age, max) {
  const pct = Math.min((age / max) * 100, 100);
  const color = pct > 75 ? 'var(--critical)' : pct > 40 ? 'var(--warn)' : 'var(--accent)';
  return (
    <div className="flex-center gap-8">
      <span className="mono" style={{ fontSize: 12, width: 32, textAlign: 'right', color: pct > 75 ? 'var(--critical)' : 'var(--text)' }}>{age}d</span>
      <div className="bar-bg" style={{ minWidth: 80 }}>
        <div className="bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

export default function Top20Page({ api, uploadId, teamOnly, jiraUrl }) {
  const [issues, setIssues] = useState([]);
  const [assigneeIssues, setAssigneeIssues] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadingAssignee, setLoadingAssignee] = useState(false);
  const [sortBy, setSortBy] = useState('age');
  const [sortDir, setSortDir] = useState('desc');
  const toggleDir = () => setSortDir(d => d === 'desc' ? 'asc' : 'desc');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');

  useEffect(() => { setFilterAssignee('all'); setAssigneeIssues(null); }, [uploadId, teamOnly]);

  // Load all open incidents for the default view
  useEffect(() => {
    if (!uploadId) { setIssues([]); return; }
    setLoading(true); setLoadError(false);
    fetch(`${api}/api/uploads/${uploadId}/tickets?incident_filter=total&team_only=${!!teamOnly}`)
      .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(d => { setIssues(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => { setLoadError(true); setLoading(false); });
  }, [uploadId, api, teamOnly]);

  // When assignee selected → fetch ALL their open incidents
  useEffect(() => {
    if (filterAssignee === 'all') { setAssigneeIssues(null); return; }
    if (!uploadId) return;
    setLoadingAssignee(true);
    fetch(`${api}/api/uploads/${uploadId}/tickets?incident_filter=total&assignee=${encodeURIComponent(filterAssignee)}`)
      .then(r => r.json())
      .then(d => { setAssigneeIssues(Array.isArray(d) ? d : []); setLoadingAssignee(false); })
      .catch(() => setLoadingAssignee(false));
  }, [filterAssignee, uploadId, api]);

  const globalJiraUrl = jiraUrl || localStorage.getItem('jiraUrl') || '';

  if (!uploadId) return <div className="empty"><p>Select an export to view the oldest issues.</p></div>;
  if (loading) return <div className="loading">Loading top 20…</div>;
  if (loadError) return <div className="empty"><p className="error-text">Couldn’t load incidents — check the connection and reload.</p></div>;

  const TEAM_MEMBERS = [
    'Aurélien BARBIEUX', 'Dimitri DE BACKER',
    'Jonathan DEMORTIER', 'Leslie BASHORUN', 'Mohamed ATAUIL', 'Pierre BUIDIN',
  ];
  // Always show all team members; add any non-team assignees found in the data
  const extraAssignees = issues.map(i => i.assignee).filter(a => a && !TEAM_MEMBERS.includes(a));
  const assignees = ['all', ...TEAM_MEMBERS, ...new Set(extraAssignees)];

  // Use assigneeIssues when an assignee is selected, otherwise use top20
  const baseIssues = filterAssignee !== 'all' && assigneeIssues ? assigneeIssues : issues;
  const priorities = ['all', ...new Set(baseIssues.map(i => i.priority))];

  let filtered = filterPriority === 'all' ? baseIssues : baseIssues.filter(i => i.priority === filterPriority);
  const dir = sortDir === 'desc' ? -1 : 1;
  if (sortBy === 'age')      filtered = [...filtered].sort((a, b) => (b.age_days - a.age_days) * dir);
  if (sortBy === 'time')     filtered = [...filtered].sort((a, b) => (b.time_spent_seconds - a.time_spent_seconds) * dir);
  if (sortBy === 'priority') filtered = [...filtered].sort((a, b) => (PRIORITY_ORDER.indexOf(a.priority) - PRIORITY_ORDER.indexOf(b.priority)) * dir);

  const maxAge = filtered.reduce((m, i) => Math.max(m, i.age_days), 1);
  const oldestIssue = filtered[0];
  const totalTime = filtered.reduce((s, i) => s + (i.time_spent_seconds || 0), 0);

  const fmtTotal = (s) => {
    s = Math.round(s);
    const w = Math.floor(s / (5*8*3600)); s %= 5*8*3600;
    const d = Math.floor(s / (8*3600)); s %= 8*3600;
    const h = Math.floor(s / 3600);
    return [w && `${w}w`, d && `${d}d`, h && `${h}h`].filter(Boolean).join(' ') || '—';
  };

  const isAssigneeView = filterAssignee !== 'all';
  const tableTitle = isAssigneeView
    ? `Open Incidents — ${filterAssignee.split(' ').pop()} (${filtered.length})`
    : `Open Incidents (${filtered.length})`;

  return (
    <div>
      <div className="grid-3" style={{ marginBottom: 24 }}>
        <div className="card" style={{ borderLeft: '3px solid var(--accent)' }}>
          <div className="card-title">Showing</div>
          <div className="card-value" style={{ color: 'var(--accent)' }}>{filtered.length}</div>
          <div className="card-sub">open incidents</div>
        </div>
        <div className="card" style={{ borderLeft: '3px solid var(--critical)' }}>
          <div className="card-title">Oldest</div>
          <div className="card-value" style={{ color: 'var(--critical)', fontSize: 22 }}>{oldestIssue ? `${Math.round(oldestIssue.age_days)}d` : '—'}</div>
          <div className="card-sub">{oldestIssue ? oldestIssue.key : 'no issues'}</div>
        </div>
        <div className="card" style={{ borderLeft: '3px solid var(--teal)' }}>
          <div className="card-title">Total Time Logged</div>
          <div className="card-value" style={{ color: 'var(--teal)', fontSize: 22 }}>{fmtTotal(totalTime)}</div>
          <div className="card-sub">across all filtered</div>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div className="section-title" style={{ margin: 0 }}>{tableTitle}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <select className="upload-select" value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}>
              {assignees.map(a => <option key={a} value={a}>{a === 'all' ? 'All assignees' : a}</option>)}
            </select>
            <select className="upload-select" value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
              {priorities.map(p => <option key={p} value={p}>{p === 'all' ? 'All priorities' : p}</option>)}
            </select>
            <select className="upload-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="age">Sort: Age</option>
              <option value="time">Sort: Time spent</option>
              <option value="priority">Sort: Priority</option>
            </select>
            <button onClick={toggleDir} title={sortDir === 'desc' ? 'Descending' : 'Ascending'}
              className="drawer-sort-btn">
              {sortDir === 'desc' ? '↓' : '↑'}
            </button>
          </div>
        </div>

        {loadingAssignee ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-2)' }}>Loading all incidents…</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Issue</th>
                  <th>Summary</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Assignee</th>
                  <th>Created</th>
                  <th>Age</th>
                  <th>Time Logged</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((issue, idx) => (
                  <tr key={issue.key} className={idx < 3 ? 'tr-critical' : ''}>
                    <td><span className={`rank rank-${Math.min(idx + 1, 3)}`}>#{idx + 1}</span></td>
                    <td>
                    {issue.url
                      ? <a href={issue.url} target="_blank" rel="noopener noreferrer" className="badge badge-blue" style={{ textDecoration: 'none' }}>{issue.key}</a>
                      : <span className="badge badge-blue">{issue.key}</span>}
                  </td>
                    <td style={{ width: '99%' }}>
                      <span style={{ fontSize: 12.5, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={issue.summary}>
                        {issue.summary}
                      </span>
                    </td>
                    <td><span className="badge badge-default">{issue.status}</span></td>
                    <td>{priorityBadge(issue.priority)}</td>
                    <td className="muted" style={{ fontSize: 12.5 }}>{issue.assignee}</td>
                    <td className="mono" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDate(issue.created)}</td>
                    <td>{ageBar(issue.age_days, maxAge)}</td>
                    <td className="mono" style={{ fontSize: 12 }}>{issue.time_spent || '—'}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--text-2)', padding: 32 }}>No issues match the filter.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid-2" style={{ marginTop: 24 }}>
        <AgingIncidentsWidget api={api} uploadId={uploadId} jiraUrl={globalJiraUrl} teamOnly={teamOnly} />
        <IncidentMonthlyWidget api={api} uploadId={uploadId} teamOnly={teamOnly} />
      </div>
    </div>
  );
}
